import { type DiscoveryWorkCard, type HomeWorkCard } from "@loomua/contracts";
import { toSimplifiedChineseText } from "./chineseText";
import { Icon } from "./icons";

type DisplayWork = HomeWorkCard | DiscoveryWorkCard;
type DisplayLanguage = DisplayWork["sourceLanguage"];

export function workTicketDisplayText(text: string, language: DisplayLanguage) {
  return language === "zh-CN" ? toSimplifiedChineseText(text) : text;
}

function localizedNumber(value: number, language: DisplayLanguage) {
  return value.toLocaleString(language === "zh-CN" ? "zh-CN" : "en");
}

function isSeries(work: DisplayWork) {
  return (
    work.status === "ongoing" ||
    work.stats.releaseCount > 1 ||
    work.stats.chapterCount > 1
  );
}

export function WorkTicketBadges({
  work,
  language,
  className = "work-ticket-badges"
}: {
  work: DisplayWork;
  language: DisplayLanguage;
  className?: string;
}) {
  if (!work.ticketBadges.length) return null;

  return (
    <p className={className}>
      {work.ticketBadges.map((badge) => {
        const label = workTicketDisplayText(badge.label, language);
        return (
          <span
            className={`work-ticket-badge is-${badge.kind}`}
            key={`${badge.kind}:${label}`}
          >
            {label}
          </span>
        );
      })}
    </p>
  );
}

export function WorkTicketCreator({
  work,
  language,
  className = "work-ticket-creator"
}: {
  work: DisplayWork;
  language: DisplayLanguage;
  className?: string;
}) {
  const creator = workTicketDisplayText(work.creator, language);

  return (
    <p className={className}>
      {work.creatorAvatarUrl ? (
        <img
          alt=""
          className="work-ticket-avatar"
          decoding="async"
          loading="lazy"
          src={work.creatorAvatarUrl}
        />
      ) : (
        <span className="work-ticket-avatar is-empty">
          <Icon name="account" />
        </span>
      )}
      <span>{creator}</span>
    </p>
  );
}

export function WorkTicketStats({
  work,
  language,
  className = "work-ticket-stats"
}: {
  work: DisplayWork;
  language: DisplayLanguage;
  className?: string;
}) {
  const zh = language === "zh-CN";
  const parts: string[] = [];

  if (isSeries(work) && work.stats.releaseCount > 1) {
    parts.push(
      zh
        ? `${localizedNumber(work.stats.releaseCount, language)} 篇`
        : `${localizedNumber(work.stats.releaseCount, language)} release${
            work.stats.releaseCount === 1 ? "" : "s"
          }`
    );
  } else if (isSeries(work) && work.stats.chapterCount > 1) {
    parts.push(
      zh
        ? `${localizedNumber(work.stats.chapterCount, language)} 章`
        : `${localizedNumber(work.stats.chapterCount, language)} episode${
            work.stats.chapterCount === 1 ? "" : "s"
          }`
    );
  }
  if (work.stats.sourceUnitCount > 0) {
    parts.push(
      zh
        ? `${localizedNumber(work.stats.sourceUnitCount, language)} 字`
        : `${localizedNumber(work.stats.sourceUnitCount, language)} words`
    );
  }
  if (work.stats.estimatedReadingMinutes > 0) {
    parts.push(
      zh
        ? `${localizedNumber(work.stats.estimatedReadingMinutes, language)} 分钟`
        : `${localizedNumber(work.stats.estimatedReadingMinutes, language)} min`
    );
  }
  if (work.stats.kudosCount > 0) {
    parts.push(`♥ ${localizedNumber(work.stats.kudosCount, language)}`);
  }

  if (!parts.length) return null;

  return <p className={className}>{parts.join("  ")}</p>;
}
